import { toPercentage, toCurrencyTruncated } from '@/utilities/formatting';
import { shallowEqual, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { getUTCDate } from '@utilities/date';
import { pathConstructor } from '@/hooks/useCompany/utils';
import type { LocationState } from 'redux-first-router';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';

type Selected =
  | { status: 'loading' }
  | {
      marketCapListing: number;
      marketCapListingFormatted: string;
      tradingItemCurrencyISO: string;
      status: 'success';
      return7D: number | null;
      return7DFormatted: string;
      return1Y: number | null;
      return1YFormatted: string;
      companyId: string;
      analystCount: number;
      lastAnalysisUpdateFormatted: string;
      sharePriceFormatted: string;
    };

const selector = (state: {
  company: CompanyState;
  location: LocationState;
}): Selected => {
  try {
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const summary = state.company.summary[companyId];
    const analysis = state.company.analysis[companyId];
    const formatting = state.company.formatting[companyId];
    const tradingItemCurrencyISO = formatting.tradingItemCurrencyISO;
    if (companyId === '') {
      return {
        status: 'loading',
      };
    }
    const formatter = (value: number | null) =>
      value !== null ? toPercentage(value, 1) : 'n/a';
    const formatTimestamp = (timestamp: number): string => {
      if (typeof timestamp !== 'number' || timestamp === 0) return 'N/A';
      return format(getUTCDate(timestamp), 'dd MMM, yyyy');
    };
    return {
      marketCapListing: analysis.marketCapRawData.listing,
      marketCapListingFormatted: toCurrencyTruncated(
        analysis.marketCapRawData.listing,
        1,
        tradingItemCurrencyISO
      ),
      tradingItemCurrencyISO,
      status: 'success',
      return7D: analysis.return7D,
      return7DFormatted: formatter(analysis.return7D),
      return1Y: analysis.return1YrAbs,
      return1YFormatted: formatter(analysis.return1YrAbs),
      companyId,
      analystCount: summary.analystCount,
      lastAnalysisUpdateFormatted: formatTimestamp(summary.lastAnalysisUpdate),
      sharePriceFormatted: toCurrencyTruncated(
        analysis.sharePrice,
        2,
        tradingItemCurrencyISO
      ),
    };
  } catch {
    return { status: 'loading' };
  }
};

export const useMetricData = () =>
  useSelector<Parameters<typeof selector>[0], ReturnType<typeof selector>>(
    selector,
    shallowEqual
  );
